// data-fit LLC · DecisionFlow · ADMIN Stage
// FiscalPeriodManager.jsx · DDL: R49 · API: v1.27 · Session 93 · S581
// Auth: useAuth() direct — ARCH-2 compliant
// Key constraints: fiscal_year_id/fiscal_period_id integers; open→closed→locked only; close/lock bodies are {}
// Open Items: OI-ADMIN-FISCAL-YEAR-LIST, OI-ADMIN-PERIOD-LIST, OI-ADMIN-PERIOD-CLOSE, OI-ADMIN-PERIOD-LOCK
// S581: Roll-forward action added per architecture_spec_v2_7.md DR-006.
//   - CONFIRM_ACTION_TYPE.ROLL_FORWARD added (source period must be locked)
//   - Roll-forward confirmation modal inline with existing renderConfirmation pattern
//   - POST /api/v1/fiscal-periods/{id}/roll-forward called on confirm
//   - On success: periods reloaded, rollForwardResult banner shown with carry-forward counts
//   - Roll-Forward button shown in Actions column when period_status === 'locked'
//   - Roll-Forward button also shown in side panel detail when period_status === 'locked'
// M040 C2: dark: prefix removed, hardcoded colors → tokens, text-* → text-tok-*, all <td> get text-[rgb(var(--color-text))]
// S647 — OI-FISCAL-PERIOD-ROLLFORWARD-UX:
//   1. Roll Forward button disabled when roll-forward already executed: derived from
//      period list — locked period whose next period (by period_start_date) is
//   2. On success, selectedFiscalYearId auto-switches to the new fiscal year if it differs,
//      so all post-roll-forward operations land on the next period, not the source.
//   3. If NEXT_FISCAL_PERIOD_NOT_FOUND, scaffolding form appears inline: CA enters next
//      fiscal year label, start date (pre-filled = source year_end + 1 day), end date
//      (pre-filled = start + 1 year - 1 day, editable). On confirm: POST /fiscal-years →
//      POST /fiscal-years/{id}/generate-periods (period_type from source period) →
//      POST /fiscal-periods/{m01_id}/roll-forward. All fiscal date logic is CA-owned —
//      no calendar-year assumptions anywhere.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthContext.jsx'
import { SCREEN_ORDER } from './AdminFlowShell'
import { ChevronUp, ChevronDown, ChevronsUpDown, Search, X } from 'lucide-react'

const PERIOD_STATUS = { OPEN: 'open', CLOSED: 'closed', LOCKED: 'locked' }
const CONFIRM_ACTION_TYPE = { CLOSE: 'close', CERTIFY_SUBMIT: 'certify_submit', CERTIFY_APPROVE: 'certify_approve', ROLL_FORWARD: 'roll_forward' }

const CLOSE_WARNING_TEXT              = 'Closing this period prevents new data entry. This action cannot be reversed.'
const CERTIFY_SUBMIT_WARNING_TEXT     = 'You are submitting this period for Certify & Close. All three prerequisites must be met: completed final calculation run, released statement batch, and approved payroll export. A peer comp admin must approve before the period is closed.'
const CERTIFY_APPROVE_WARNING_TEXT    = 'You are approving and closing this period on behalf of your organisation. This action is irreversible. Ensure you have independently reviewed the period results before confirming.'
const LOCK_WARNING_TEXT               = 'Locking this period permanently prevents any further changes. This action cannot be reversed.'
const ROLL_FORWARD_WARNING_TEXT = 'Rolling forward will activate the next period and carry forward plans (as draft), plan assignments, and position calculation configs. Plans will require re-approval before calculation is eligible. Quota and credits are NOT carried forward — load fresh data for the new period.'
const INVALID_CLOSE_TRANSITION_MESSAGE        = 'This period cannot be closed — its status has changed. Reload and try again.'
const INVALID_LOCK_TRANSITION_MESSAGE         = 'This period cannot be locked — its status has changed. Reload and try again.'
const CERTIFY_ALREADY_SUBMITTED_MESSAGE       = 'This period has already been submitted for certification. A peer comp admin must approve.'
const CERTIFY_NOT_SUBMITTED_MESSAGE           = 'This period has not been submitted for certification. Submit first before approving.'
const SELF_APPROVAL_NOT_PERMITTED_MESSAGE     = 'Self-approval is not permitted. The comp admin who submitted certification cannot also approve it.'
const ROLLFORWARD_ALREADY_EXECUTED_MESSAGE  = 'Roll-forward has already been executed for this period. An active wizard session already exists for the target period.'
const NEXT_FISCAL_PERIOD_NOT_OPEN_MESSAGE   = 'The next fiscal period is not open. Roll-forward requires the next period to be open.'
const ROLLFORWARD_FAILED_MESSAGE            = 'The roll-forward transaction failed. No changes were committed.'


// ---------------------------------------------------------------------------
// usePeriodReadiness — S853 OI-S839-PERIOD-READY-UI-WORKFLOW
// Fetches GET /api/v1/fiscal-periods/{id}/period-readiness.
// Called inside renderConfirmation() for CERTIFY_SUBMIT and CERTIFY_APPROVE.
// Non-blocking: gate display is informational — server enforces authoritatively.
// ---------------------------------------------------------------------------

function usePeriodReadiness(periodId) {
  const [readiness, setReadiness] = React.useState(null)
  const [loading, setLoading]     = React.useState(false)

  React.useEffect(() => {
    if (!periodId) return
    let cancelled = false
    setLoading(true)
    setReadiness(null)
    fetch(`/api/v1/fiscal-periods/${periodId}/period-readiness`, { credentials: 'include' })
      .then((r) => r.json().catch(() => ({})))
      .then((data) => { if (!cancelled) setReadiness(data) })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [periodId])

  return { readiness, loading }
}

// Gate display — rendered inside confirmation modal for certify actions.
// Three rows: final run, statement batch, payroll export.
function PeriodGateChecklist({ periodId }) {
  const { readiness, loading } = usePeriodReadiness(periodId)

  const GATES = [
    { key: 'gate_final_run',       label: 'Completed final calculation run' },
    { key: 'gate_statement_batch', label: 'Released statement batch'         },
    { key: 'gate_payroll_export',  label: 'Approved or exported payroll'     },
  ]

  if (loading) return <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Checking prerequisites...</p>
  if (!readiness?.gates) return null

  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Prerequisites</p>
      {GATES.map(({ key, label }) => {
        const passed = readiness.gates[key]?.passed === true
        return (
          <div key={key} className="flex items-start gap-2">
            <span className={`mt-0.5 text-tok-xs font-bold flex-shrink-0 ${passed ? 'text-[rgb(var(--color-success))]' : 'text-[rgb(var(--color-danger))]'}`}>
              {passed ? '\u2713' : '\u2717'}
            </span>
            <div className="min-w-0">
              <span className={`text-tok-xs ${passed ? 'text-[rgb(var(--color-text))]' : 'text-[rgb(var(--color-danger))]'}`}>{label}</span>
              {!passed && readiness.gates[key]?.detail && (
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">{readiness.gates[key].detail}</p>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers — date arithmetic (fiscal-calendar-safe, no calendar assumptions)
// ---------------------------------------------------------------------------

/** Add one day to a yyyy-mm-dd string. Uses UTC to avoid DST shifts. */
const addOneDay = (isoDate) => {
  const d = new Date(isoDate + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() + 1)
  return d.toISOString().slice(0, 10)
}

/** Add one year then subtract one day from a yyyy-mm-dd string. */
const addOneYearMinusOneDay = (isoDate) => {
  const d = new Date(isoDate + 'T00:00:00Z')
  d.setUTCFullYear(d.getUTCFullYear() + 1)
  d.setUTCDate(d.getUTCDate() - 1)
  return d.toISOString().slice(0, 10)
}

// ---------------------------------------------------------------------------
// API response helpers (unchanged from S581)
// ---------------------------------------------------------------------------

const getErrorEnvelope = (err) => {
  if (err?.error) { const { code, message } = err.error; return { code, message } }
  return { code: 'UNKNOWN_ERROR', message: 'An unexpected error occurred.' }
}

const parseApiResponse = async (response) => {
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw payload?.error ? payload : { error: { code: 'HTTP_ERROR', http_status: response.status, message: 'Request failed.' } }
  }
  return payload
}

const getRowsFromEnvelope = (payload) => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.results)) return payload.results
  return []
}

const getPaginationFromEnvelope = (payload) => ({
  next_cursor: payload?.pagination?.next_cursor || null,
  has_more: Boolean(payload?.pagination?.has_more),
})

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function FiscalPeriodManager({ aiStatus, aiResponse, currentScreen, onNavigate }) {
  const { token, user } = useAuth()
  const currentUserId = user?.user_id ? String(user.user_id) : null

  // ── Core state (unchanged from S581) ──────────────────────────────────────
  const [fiscalYears, setFiscalYears] = useState([])
  const [selectedFiscalYearId, setSelectedFiscalYearId] = useState(null)
  const [periods, setPeriods] = useState([])
  const [nextCursor, setNextCursor] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [isLoadingYears, setIsLoadingYears] = useState(false)
  const [isLoadingPeriods, setIsLoadingPeriods] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [actionError, setActionError] = useState(null)
  const [confirmAction, setConfirmAction] = useState(null)
  const [rollForwardResult, setRollForwardResult] = useState(null)

  // ── S647 — scaffolding state (new) ────────────────────────────────────────
  // needsScaffolding: true when roll-forward returned NEXT_FISCAL_PERIOD_NOT_FOUND
  // scaffoldForm: CA-editable fields for the new fiscal year
  // scaffoldingInProgress: true while the 3-step create→generate→rollforward runs
  // scaffoldError: step-level error message shown inside the scaffolding form
  const [needsScaffolding, setNeedsScaffolding] = useState(false)
  const [scaffoldForm, setScaffoldForm] = useState({ label: '', start_date: '', end_date: '' })
  const [scaffoldingInProgress, setScaffoldingInProgress] = useState(false)
  const [scaffoldError, setScaffoldError] = useState(null)

  // ── loadPeriods (unchanged from S581) ─────────────────────────────────────
  const loadPeriods = useCallback(async ({ cursor = null, append = false } = {}) => {
    if (selectedFiscalYearId === null) return
    if (append) setIsLoadingMore(true); else setIsLoadingPeriods(true)
    setErrorMessage(null)
    try {
      const queryParams = new URLSearchParams({ fiscal_year_id: String(selectedFiscalYearId) })
      if (cursor) queryParams.set('cursor', cursor)
      const response = await fetch(`/api/v1/fiscal-periods?${queryParams.toString()}`, { method: 'GET', credentials: 'include' })
      const payload = await parseApiResponse(response)
      const rows = getRowsFromEnvelope(payload)
      const pagination = getPaginationFromEnvelope(payload)
      setPeriods((current) => (append ? [...current, ...rows] : rows))
      setNextCursor(pagination.next_cursor)
      setHasMore(pagination.has_more)
    } catch (err) {
      const { code, message } = getErrorEnvelope(err)
      setErrorMessage(message || `Unable to load fiscal periods. (${code})`)
    } finally {
      setIsLoadingPeriods(false)
      setIsLoadingMore(false)
    }
  }, [selectedFiscalYearId, token])

  // ── loadFiscalYears (unchanged from S581) ─────────────────────────────────
  useEffect(() => {
    const loadFiscalYears = async () => {
      setIsLoadingYears(true)
      setErrorMessage(null)
      try {
        const response = await fetch('/api/v1/fiscal-years', { method: 'GET', credentials: 'include' })
        const payload = await parseApiResponse(response)
        const rows = getRowsFromEnvelope(payload)
        setFiscalYears(rows)
        if (rows.length > 0) {
          const firstActive = rows.find((y) => y.is_active === true)
          setSelectedFiscalYearId(Number((firstActive || rows[0]).fiscal_year_id))
        }
      } catch (err) {
        const { code, message } = getErrorEnvelope(err)
        setErrorMessage(message || `Unable to load fiscal years. (${code})`)
      } finally {
        setIsLoadingYears(false)
      }
    }
    loadFiscalYears()
  }, [token])

  useEffect(() => {
    if (selectedFiscalYearId === null) return
    setPeriods([]); setNextCursor(null); setHasMore(false)
    loadPeriods({ append: false })
  }, [selectedFiscalYearId, loadPeriods])

  // ── handleFiscalYearChange (unchanged from S581) ───────────────────────────
  const handleFiscalYearChange = (event) => {
    const value = event.target.value
    if (value === '') { setSelectedFiscalYearId(null); return }
    setSelectedFiscalYearId(Number(value))
  }

  // ── openConfirmAction (unchanged from S581) ────────────────────────────────
  const openConfirmAction = (type, period) => {
    setActionError(null)
    setNeedsScaffolding(false)
    setScaffoldError(null)
    setConfirmAction({ type, period: { fiscal_period_id: Number(period.fiscal_period_id), period_code: period.period_code, period_label: period.period_label, period_status: period.period_status, period_type: period.period_type } })
  }

  // ── handleConfirmSubmit — S647: NEXT_FISCAL_PERIOD_NOT_FOUND triggers scaffolding ──
  const handleConfirmSubmit = async () => {
    if (!confirmAction || selectedFiscalYearId === null) return
    const { type, period } = confirmAction
    if (type === CONFIRM_ACTION_TYPE.CLOSE && period.period_status !== PERIOD_STATUS.OPEN)   { setActionError(INVALID_CLOSE_TRANSITION_MESSAGE); return }
    if (type === CONFIRM_ACTION_TYPE.CERTIFY_SUBMIT  && period.period_status !== PERIOD_STATUS.OPEN) { setActionError(INVALID_CLOSE_TRANSITION_MESSAGE); return }
    if (type === CONFIRM_ACTION_TYPE.CERTIFY_APPROVE && period.period_status !== PERIOD_STATUS.OPEN) { setActionError(INVALID_CLOSE_TRANSITION_MESSAGE); return }
    if (type === CONFIRM_ACTION_TYPE.ROLL_FORWARD && period.period_status !== PERIOD_STATUS.LOCKED) { setActionError('This period cannot be rolled forward — it must be locked first.'); return }
    setIsSubmitting(true); setActionError(null); setRollForwardResult(null)
    try {
      const periodId = Number(period.fiscal_period_id)
      let endpoint, method, bodyObj
      if (type === CONFIRM_ACTION_TYPE.CERTIFY_SUBMIT) {
        endpoint = `/api/v1/fiscal-periods/${periodId}/certify`; method = 'PATCH'; bodyObj = { action: 'submit' }
      } else if (type === CONFIRM_ACTION_TYPE.CERTIFY_APPROVE) {
        endpoint = `/api/v1/fiscal-periods/${periodId}/certify`; method = 'PATCH'; bodyObj = { action: 'approve' }
      } else if (type === CONFIRM_ACTION_TYPE.CLOSE) {
        endpoint = `/api/v1/fiscal-periods/${periodId}/close`; method = 'PATCH'; bodyObj = {}
      } else {
        endpoint = `/api/v1/fiscal-periods/${periodId}/roll-forward`; method = 'POST'; bodyObj = null
      }
      const fetchOptions = { method, credentials: 'include' }
      if (bodyObj !== null) fetchOptions.body = JSON.stringify(bodyObj)
      const response = await fetch(endpoint, fetchOptions)
      const result   = await parseApiResponse(response)
      if (type === CONFIRM_ACTION_TYPE.ROLL_FORWARD) {
        setRollForwardResult(result)
        // S647: auto-switch to the new fiscal year if it differs from current selection.
        // result.rollforward_summary.target_fiscal_period_id is the new period —
        // find its fiscal year from the reloaded period list after loadPeriods completes.
        // We store the target_fiscal_period_id and resolve it post-reload below.
      }
      setConfirmAction(null)
      setNeedsScaffolding(false)
      // S647: reload periods then switch fiscal year to where the new period lives.
      if (type === CONFIRM_ACTION_TYPE.ROLL_FORWARD) {
        await _reloadAndSwitchToNewPeriod(result.rollforward_summary?.target_fiscal_period_id)
      } else {
        await loadPeriods({ append: false })
      }
    } catch (err) {
      const { code, message } = getErrorEnvelope(err)
      if (code === 'INVALID_STATUS_TRANSITION' && confirmAction.type === CONFIRM_ACTION_TYPE.CLOSE)           setActionError(INVALID_CLOSE_TRANSITION_MESSAGE)
      else if (code === 'INVALID_STATUS_TRANSITION' && confirmAction.type === CONFIRM_ACTION_TYPE.CERTIFY_SUBMIT)  setActionError(INVALID_CLOSE_TRANSITION_MESSAGE)
      else if (code === 'INVALID_STATUS_TRANSITION' && confirmAction.type === CONFIRM_ACTION_TYPE.CERTIFY_APPROVE) setActionError(INVALID_CLOSE_TRANSITION_MESSAGE)
      else if (code === 'CERTIFY_ALREADY_SUBMITTED')     setActionError(CERTIFY_ALREADY_SUBMITTED_MESSAGE)
      else if (code === 'CERTIFY_NOT_SUBMITTED')         setActionError(CERTIFY_NOT_SUBMITTED_MESSAGE)
      else if (code === 'SELF_APPROVAL_NOT_PERMITTED')   setActionError(SELF_APPROVAL_NOT_PERMITTED_MESSAGE)
      else if (code === 'ROLLFORWARD_ALREADY_EXECUTED')  setActionError(ROLLFORWARD_ALREADY_EXECUTED_MESSAGE)
      else if (code === 'NEXT_FISCAL_PERIOD_NOT_FOUND') {
        // S647: trigger scaffolding instead of a dead-end error message.
        // Pre-fill start_date from the current fiscal year's year_end_date + 1 day.
        // Pre-fill end_date as start_date + 1 year - 1 day (CA must verify for their fiscal calendar).
        // Label is intentionally blank — the CA owns their fiscal year naming convention.
        const sourceYear = fiscalYears.find((y) => Number(y.fiscal_year_id) === selectedFiscalYearId)
        const suggestedStart = sourceYear?.year_end_date ? addOneDay(sourceYear.year_end_date) : ''
        const suggestedEnd   = suggestedStart ? addOneYearMinusOneDay(suggestedStart) : ''
        setScaffoldForm({ label: '', start_date: suggestedStart, end_date: suggestedEnd })
        setNeedsScaffolding(true)
        setScaffoldError(null)
      }
      else if (code === 'NEXT_FISCAL_PERIOD_NOT_OPEN')    setActionError(NEXT_FISCAL_PERIOD_NOT_OPEN_MESSAGE)
      else if (code === 'ROLLFORWARD_TRANSACTION_FAILED') setActionError(ROLLFORWARD_FAILED_MESSAGE)
      else setActionError(message || `Unable to update fiscal period. (${code})`)
    } finally {
      setIsSubmitting(false)
    }
  }

  // ── _reloadAndSwitchToNewPeriod — S647 helper ─────────────────────────────
  // After a successful roll-forward, reload fiscal years (new one may have been
  // created by scaffolding), then fetch the target period's fiscal_year_id and
  // switch the selector. All post-roll-forward ops land on the new period.
  const _reloadAndSwitchToNewPeriod = async (targetFiscalPeriodId) => {
    try {
      // Reload fiscal year list in case scaffolding created a new one
      const fyResponse = await fetch('/api/v1/fiscal-years', { method: 'GET', credentials: 'include' })
      const fyPayload  = await parseApiResponse(fyResponse)
      const fyRows     = getRowsFromEnvelope(fyPayload)
      setFiscalYears(fyRows)

      if (!targetFiscalPeriodId) {
        // Fallback: just reload current year's periods
        await loadPeriods({ append: false })
        return
      }

      // Find which fiscal year the target period belongs to by scanning all years' periods.
      // We do this by fetching periods for each year until we find the target.
      // In practice this is at most 2 fetches (current year + next year).
      let foundYearId = null
      for (const fy of fyRows) {
        const fpResponse = await fetch(
          `/api/v1/fiscal-periods?fiscal_year_id=${fy.fiscal_year_id}`,
          { method: 'GET', credentials: 'include' }
        )
        const fpPayload = await parseApiResponse(fpResponse)
        const fpRows    = getRowsFromEnvelope(fpPayload)
        const match     = fpRows.find((p) => Number(p.fiscal_period_id) === Number(targetFiscalPeriodId))
        if (match) {
          foundYearId = Number(fy.fiscal_year_id)
          // If this is already the selected year, update periods directly
          if (foundYearId === selectedFiscalYearId) {
            setPeriods(fpRows)
          }
          break
        }
      }

      if (foundYearId && foundYearId !== selectedFiscalYearId) {
        // This triggers the useEffect which calls loadPeriods for the new year
        setSelectedFiscalYearId(foundYearId)
      } else {
        await loadPeriods({ append: false })
      }
    } catch {
      // Non-fatal: fall back to reloading current year
      await loadPeriods({ append: false })
    }
  }

  // ── handleScaffoldAndRollForward — S647: 3-step reactive fiscal year creation ──
  // Step 1: POST /api/v1/fiscal-years        — create next fiscal year
  // Step 2: POST /fiscal-years/{id}/generate-periods — generate 12 periods (period_type from source)
  // Step 3: POST /fiscal-periods/{m01_id}/roll-forward — roll forward to first generated period
  // period_type inherited from source period — CA does not re-select it.
  // All date/label values come from scaffoldForm — no server-side assumptions.
  const handleScaffoldAndRollForward = async () => {
    if (!confirmAction) return
    const { period } = confirmAction

    // Client-side validation before any API call
    if (!scaffoldForm.label.trim()) { setScaffoldError('Fiscal year label is required.'); return }
    if (!scaffoldForm.start_date)   { setScaffoldError('Start date is required.'); return }
    if (!scaffoldForm.end_date)     { setScaffoldError('End date is required.'); return }
    if (scaffoldForm.end_date <= scaffoldForm.start_date) { setScaffoldError('End date must be after start date.'); return }

    setScaffoldingInProgress(true)
    setScaffoldError(null)

    try {
      // Step 1 — Create fiscal year
      const fyResponse = await fetch('/api/v1/fiscal-years', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fiscal_year_label: scaffoldForm.label.trim(),
          year_start_date:   scaffoldForm.start_date,
          year_end_date:     scaffoldForm.end_date,
          is_active:         true,
        }),
      })
      const fyResult = await parseApiResponse(fyResponse)
      const newFiscalYearId = fyResult.fiscal_year_id
      if (!newFiscalYearId) throw new Error('Fiscal year created but no fiscal_year_id returned.')

      // Step 2 — Generate periods (idempotent; period_type from source period)
      const periodType = period.period_type || 'monthly'
      const gpResponse = await fetch(`/api/v1/fiscal-years/${newFiscalYearId}/generate-periods`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period_type: periodType }),
      })
      const gpResult = await parseApiResponse(gpResponse)
      // generate-periods returns periods[] sorted by insertion order = period_start_date ASC.
      // periods[0] is always the first period (M01 or Q1) — confirmed from router source.
      const generatedPeriods = gpResult.periods || []
      if (generatedPeriods.length === 0) throw new Error('No periods were generated. Check fiscal year dates.')
      const firstPeriodId = generatedPeriods[0].fiscal_period_id
      if (!firstPeriodId) throw new Error('Generated periods missing fiscal_period_id.')

      // Step 3 — Roll forward to first generated period
      const rfResponse = await fetch(`/api/v1/fiscal-periods/${firstPeriodId}/roll-forward`, {
        method: 'POST',
        credentials: 'include',
      })
      const rfResult = await parseApiResponse(rfResponse)

      // Success — update UI
      setRollForwardResult(rfResult)
      setConfirmAction(null)
      setNeedsScaffolding(false)
      await _reloadAndSwitchToNewPeriod(rfResult.rollforward_summary?.target_fiscal_period_id)

    } catch (err) {
      const { code, message } = getErrorEnvelope(err)
      if (code === 'FISCAL_YEAR_DUPLICATE') {
        setScaffoldError(`A fiscal year with that label already exists. Use a unique label.`)
      } else if (code === 'ROLLFORWARD_ALREADY_EXECUTED') {
        setScaffoldError(ROLLFORWARD_ALREADY_EXECUTED_MESSAGE)
      } else {
        setScaffoldError(message || `Scaffold failed. (${code})`)
      }
    } finally {
      setScaffoldingInProgress(false)
    }
  }

  // ── renderStatusBadge / renderTypeBadge (unchanged from S581) ─────────────
  const renderStatusBadge = (status) => {
    const colors = {
      open:   'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]',
      closed: 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]',
      locked: 'bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]',
    }
    return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-tok-xs font-medium ${colors[status] || 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]'}`}>{status}</span>
  }

  const renderTypeBadge = (periodType) => (
    <span className="inline-flex items-center rounded-full bg-[rgb(var(--color-surface-muted))] px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">{periodType}</span>
  )

  // ── renderConfirmation — S648: certify & close dual-approval + scaffolding ──
  const renderConfirmation = () => {
    if (!confirmAction) return null
    const isClose          = confirmAction.type === CONFIRM_ACTION_TYPE.CLOSE
    const isCertifySubmit  = confirmAction.type === CONFIRM_ACTION_TYPE.CERTIFY_SUBMIT
    const isCertifyApprove = confirmAction.type === CONFIRM_ACTION_TYPE.CERTIFY_APPROVE
    const isRollForward    = confirmAction.type === CONFIRM_ACTION_TYPE.ROLL_FORWARD
    const title = isCertifySubmit  ? 'Submit for Certify & Close'
                : isCertifyApprove ? 'Approve & Close Period'
                : isClose          ? 'Close Fiscal Period'
                : isRollForward    ? 'Roll Forward Fiscal Period'
                :                    'Lock Fiscal Period'
    const warning = isCertifySubmit  ? CERTIFY_SUBMIT_WARNING_TEXT
                  : isCertifyApprove ? CERTIFY_APPROVE_WARNING_TEXT
                  : isClose          ? CLOSE_WARNING_TEXT
                  : isRollForward    ? ROLL_FORWARD_WARNING_TEXT
                  :                    LOCK_WARNING_TEXT
    const confirmLabel = isSubmitting
      ? (isCertifySubmit ? 'Submitting…' : isCertifyApprove ? 'Approving & Closing…' : isClose ? 'Closing…' : isRollForward ? 'Rolling forward…' : 'Locking…')
      : (isCertifySubmit ? 'Submit for Certification' : isCertifyApprove ? 'Approve & Close' : isClose ? 'Confirm Close' : isRollForward ? 'Confirm Roll-Forward' : 'Confirm Lock')

    // S647: scaffolding form — shown when NEXT_FISCAL_PERIOD_NOT_FOUND on roll-forward
    if (isRollForward && needsScaffolding) {
      const busy = scaffoldingInProgress
      return (
        <div className="rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/10 p-4 flex flex-col gap-3" aria-live="polite">
          <div>
            <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Next Fiscal Year Required</h3>
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-1">
              No periods exist after <strong>{confirmAction.period.period_code}</strong>. Create the next fiscal year and DecisionFlow will generate all periods and complete the roll-forward automatically.
            </p>
          </div>
          <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-3 flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Fiscal Year Label <span className="text-[rgb(var(--color-danger))]">*</span></label>
              <input
                type="text"
                placeholder="e.g. FY2027"
                value={scaffoldForm.label}
                onChange={(e) => setScaffoldForm((f) => ({ ...f, label: e.target.value }))}
                disabled={busy}
                className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none disabled:opacity-50"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Start Date <span className="text-[rgb(var(--color-danger))]">*</span></label>
                <input
                  type="date"
                  value={scaffoldForm.start_date}
                  onChange={(e) => setScaffoldForm((f) => ({ ...f, start_date: e.target.value }))}
                  disabled={busy}
                  className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none disabled:opacity-50"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">End Date <span className="text-[rgb(var(--color-danger))]">*</span></label>
                <input
                  type="date"
                  value={scaffoldForm.end_date}
                  onChange={(e) => setScaffoldForm((f) => ({ ...f, end_date: e.target.value }))}
                  disabled={busy}
                  className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none disabled:opacity-50"
                />
              </div>
            </div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              DecisionFlow will create this fiscal year, generate all {confirmAction.period.period_type || 'monthly'} periods, and roll forward to the first one.
              Verify dates match your fiscal calendar before confirming.
            </p>
          </div>
          {scaffoldError && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{scaffoldError}</div>}
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary))]/80 disabled:opacity-50"
              onClick={handleScaffoldAndRollForward}
              disabled={busy}
            >
              {busy ? 'Creating & rolling forward…' : 'Create & Roll Forward'}
            </button>
            <button
              type="button"
              className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50"
              onClick={() => { setConfirmAction(null); setNeedsScaffolding(false); setScaffoldError(null) }}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </div>
      )
    }

    // Standard confirmation (unchanged from S581)
    return (
      <div className="rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/10 p-4 flex flex-col gap-2" aria-live="polite">
        <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{title}</h3>
        <p className="text-tok-sm text-[rgb(var(--color-text))]">{confirmAction.period.period_code} — {confirmAction.period.period_label}</p>
        <p className="text-tok-sm text-[rgb(var(--color-warning))]">{warning}</p>
        {/* S853 — OI-S839-PERIOD-READY-UI-WORKFLOW: gate checklist for certify actions */}
        {(isCertifySubmit || isCertifyApprove) && confirmAction?.period?.fiscal_period_id && (
          <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-3">
            <PeriodGateChecklist periodId={confirmAction.period.fiscal_period_id} />
          </div>
        )}
        {actionError && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{actionError}</div>}
        <div className="flex gap-2">
          <button type="button" className="rounded-lg bg-[rgb(var(--color-danger))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-danger))]/80 disabled:opacity-50" onClick={handleConfirmSubmit} disabled={isSubmitting}>
            {confirmLabel}
          </button>
          <button type="button" className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50" onClick={() => { setConfirmAction(null); setActionError(null) }} disabled={isSubmitting}>
            Cancel
          </button>
        </div>
      </div>
    )
  }

  // renderPeriodRows replaced by inline render in sortedPeriods.map() below (S579)

  // S579 — OI-UI-TABLE-CONSISTENCY: sort + search + scroll + side panel
  const [search, setSearch]       = useState('')
  const [sortCol, setSortCol]     = useState('period_code')
  const [sortDir, setSortDir]     = useState('asc')
  const [selectedPeriod, setSelectedPeriod] = useState(null)

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortCol(col); setSortDir('asc') }
  }

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronsUpDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-text-muted))]" />
    return sortDir === 'asc'
      ? <ChevronUp className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />
      : <ChevronDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />
  }

  const filteredPeriods = useMemo(() => {
    const q = search.toLowerCase()
    return periods.filter((p) =>
      !q || (p.period_code ?? '').toLowerCase().includes(q) || (p.period_label ?? '').toLowerCase().includes(q)
    )
  }, [periods, search])

  const sortedPeriods = useMemo(() => {
    return [...filteredPeriods].sort((a, b) => {
      let av = '', bv = ''
      if (sortCol === 'period_code')       { av = a.period_code ?? '';       bv = b.period_code ?? '' }
      else if (sortCol === 'period_label') { av = a.period_label ?? '';      bv = b.period_label ?? '' }
      else if (sortCol === 'start_date')   { av = a.period_start_date ?? ''; bv = b.period_start_date ?? '' }
      else if (sortCol === 'end_date')     { av = a.period_end_date ?? '';   bv = b.period_end_date ?? '' }
      else if (sortCol === 'period_type')  { av = a.period_type ?? '';       bv = b.period_type ?? '' }
      else if (sortCol === 'status')       { av = a.period_status ?? '';     bv = b.period_status ?? '' }
      else if (sortCol === 'active')       { av = a.is_active ? '1' : '0';  bv = b.is_active ? '1' : '0' }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [filteredPeriods, sortCol, sortDir])

  // S647 FIX: rollForwardDone — derived from period list, not session state.
  // A locked period has already been rolled forward when the next period
  // (immediately following by period_start_date) is is_active=TRUE.
  // This signal survives page reload, unlike the prior rollForwardResult approach.
  // Server 409 ROLLFORWARD_ALREADY_EXECUTED remains the authoritative guard.
  // Helper: given a period, find the next period in the loaded array.
  const _nextPeriod = (p) => periods
    .filter(x => x.period_start_date > p.period_start_date)
    .sort((a, b) => a.period_start_date < b.period_start_date ? -1 : 1)[0]

  const thCls = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] cursor-pointer select-none whitespace-nowrap hover:text-[rgb(var(--color-text))]'
  const tdCls = 'px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text))] whitespace-nowrap'

  return (
    <div className="flex flex-col gap-4">
      <div className="mb-1">
        <h2 className="text-tok-base font-bold text-[rgb(var(--color-text))]">Fiscal Period Manager</h2>
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-0.5">Review fiscal periods. Certify &amp; Close requires dual approval — CA One submits, CA Two approves. Roll Forward activates the next period.</p>
      </div>

      {/* Fiscal year selector */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
        <label htmlFor="fiscal-year-selector" className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Fiscal Year</label>
        {isLoadingYears ? (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading fiscal years…</p>
        ) : fiscalYears.length === 0 ? (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No fiscal years found.</p>
        ) : (
          <select id="fiscal-year-selector" value={selectedFiscalYearId ?? ''} onChange={handleFiscalYearChange} className="w-full max-w-sm rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-1.5 text-tok-sm">
            {fiscalYears.map((year) => (
              <option key={year.fiscal_year_id} value={Number(year.fiscal_year_id)}>
                {year.fiscal_year_label} ({year.year_start_date} → {year.year_end_date}){year.is_active ? ' — Active' : ''}
              </option>
            ))}
          </select>
        )}
      </div>

      {errorMessage && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{errorMessage}</div>}

      {/* Roll-forward success banner — S647: moved above table for prominence */}
      {rollForwardResult && (
        <div className="rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-surface))]">
          <div className="flex items-start justify-between border-b border-[rgb(var(--color-success))]/20 px-4 py-3">
            <div>
              <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-success))]">Roll-forward completed</h3>
              <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                {rollForwardResult.period_label || rollForwardResult.period_code} is now active. All further operations should be performed on this period.
              </p>
            </div>
            <button type="button" onClick={() => setRollForwardResult(null)} className="rounded-lg p-1 hover:bg-[rgb(var(--color-surface-muted))]">
              <X className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
            </button>
          </div>
          <div className="px-4 py-3 space-y-3">
            <div className="grid gap-2 sm:grid-cols-2 text-tok-sm">
              {[
                { label: 'Period code',                value: rollForwardResult.period_code                  || '—' },
                { label: 'Period status',              value: rollForwardResult.period_status                || '—' },
                { label: 'Plans cloned (as draft)',    value: rollForwardResult.plans_cloned                 ?? '—' },
                { label: 'Plan assignments seeded',    value: rollForwardResult.plan_assignments_seeded      ?? '—' },
                { label: 'Calc configs seeded',        value: rollForwardResult.position_calc_configs_seeded ?? '—' },
                { label: 'Wizard session',             value: rollForwardResult.wizard_session_id
                    ? `${String(rollForwardResult.wizard_session_id).slice(0, 8)}…`
                    : '—' },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between">
                  <span className="text-[rgb(var(--color-text-muted))]">{label}</span>
                  <span className="font-medium text-[rgb(var(--color-text))]">{String(value)}</span>
                </div>
              ))}
            </div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              Cloned plans are in <strong>draft</strong> status and require re-approval before calculation is eligible.
              Load fresh quota and credits for the new period.
            </p>
          </div>
        </div>
      )}

      {/* Confirmation dialog — floats above table */}
      {renderConfirmation()}

      {/* Fiscal Periods table */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Fiscal Periods {periods.length > 0 && <span className="ml-1 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">({periods.length})</span>}</h3>
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
            <input
              type="text"
              placeholder="Search periods…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-1.5 pl-8 pr-3 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
            />
          </div>
          {search && <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{sortedPeriods.length} of {periods.length}</span>}
        </div>

        <div className="overflow-auto max-h-[calc(100vh-22rem)] rounded-xl border border-[rgb(var(--color-border))]">
          <table className="w-full text-tok-sm">
            <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] z-10">
              <tr>
                {[['period_code','Code'],['period_label','Label'],['start_date','Start Date'],['end_date','End Date'],['period_type','Type'],['status','Status'],['active','Active']].map(([col, label]) => (
                  <th key={col} className={thCls} onClick={() => toggleSort(col)}>
                    {label}<SortIcon col={col} />
                  </th>
                ))}
                <th className="px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingPeriods && (
                <tr><td colSpan="8" className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">Loading fiscal periods…</td></tr>
              )}
              {!isLoadingPeriods && sortedPeriods.length === 0 && (
                <tr><td colSpan="8" className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">{search ? 'No periods match your search.' : 'No fiscal periods found for this fiscal year.'}</td></tr>
              )}
              {!isLoadingPeriods && sortedPeriods.map((period) => {
                const periodId   = Number(period.fiscal_period_id)
                // S648 — Certify & Close dual-approval action surface
                const certifySubmittedBy = period.certify_submitted_by ?? null
                const canCertifySubmit   = period.period_status === PERIOD_STATUS.OPEN && !certifySubmittedBy
                const canCertifyApprove  = period.period_status === PERIOD_STATUS.OPEN && !!certifySubmittedBy && currentUserId !== String(certifySubmittedBy)
                const awaitingPeer       = period.period_status === PERIOD_STATUS.OPEN && !!certifySubmittedBy && currentUserId === String(certifySubmittedBy)
                const canClose           = false // legacy — hidden in favour of certify flow
                const canLock            = period.period_status === PERIOD_STATUS.CLOSED
                // S647 FIX: rollFwdDone — locked period whose next period is is_active=TRUE.
                // Survives page reload. Server 409 is authoritative guard.
                const rollFwdDone = period.period_status === PERIOD_STATUS.LOCKED &&
                                    _nextPeriod(period)?.is_active === true
                const canRollFwd  = period.period_status === PERIOD_STATUS.LOCKED && !rollFwdDone
                const isSelected  = selectedPeriod?.fiscal_period_id === periodId
                return (
                  <tr
                    key={periodId}
                    className={`border-t border-[rgb(var(--color-border))] cursor-pointer transition-colors hover:bg-[rgb(var(--color-surface-muted))] ${isSelected ? 'bg-[rgb(var(--color-surface-muted))]' : ''}`}
                    onClick={() => setSelectedPeriod(isSelected ? null : period)}
                  >
                    <td className={tdCls}>{period.period_code}</td>
                    <td className={tdCls}>{period.period_label}</td>
                    <td className={tdCls}>{period.period_start_date}</td>
                    <td className={tdCls}>{period.period_end_date}</td>
                    <td className={tdCls}>{renderTypeBadge(period.period_type)}</td>
                    <td className={tdCls}>{renderStatusBadge(period.period_status)}</td>
                    <td className={tdCls}>{period.is_active ? 'Active' : 'Inactive'}</td>
                    <td className={tdCls} onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-2 flex-wrap">
                        {/* S648 — Certify & Close dual-approval buttons */}
                        {/* S853: gate status loads in confirmation modal on open */}
                        {canCertifySubmit  && <button type="button" className="rounded-lg bg-[rgb(var(--color-warning))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-warning))]/80 disabled:opacity-50" onClick={() => openConfirmAction(CONFIRM_ACTION_TYPE.CERTIFY_SUBMIT, period)} disabled={isSubmitting}>Certify &amp; Close</button>}
                        {canCertifyApprove && <button type="button" className="rounded-lg bg-[rgb(var(--color-danger))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-danger))]/80 disabled:opacity-50" onClick={() => openConfirmAction(CONFIRM_ACTION_TYPE.CERTIFY_APPROVE, period)} disabled={isSubmitting}>Approve &amp; Close</button>}
                        {awaitingPeer      && <span className="inline-flex items-center gap-1 rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-2 py-1 text-tok-xs text-[rgb(var(--color-warning))]">⏳ Awaiting peer approval</span>}
                        {canLock     && <button type="button" className="rounded-lg bg-[rgb(var(--color-danger))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-danger))]/80 disabled:opacity-50" onClick={() => openConfirmAction(CONFIRM_ACTION_TYPE.LOCK, period)} disabled={isSubmitting}>Lock</button>}
                        {canRollFwd  && <button type="button" className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary))]/80 disabled:opacity-50" onClick={() => openConfirmAction(CONFIRM_ACTION_TYPE.ROLL_FORWARD, period)} disabled={isSubmitting}>Roll Forward</button>}
                        {/* S647: locked + roll-forward already done — show completed indicator */}
                        {period.period_status === PERIOD_STATUS.LOCKED && rollFwdDone && (
                          <span className="text-tok-xs text-[rgb(var(--color-success))]">✓ Rolled forward</span>
                        )}
                        {!canCertifySubmit && !canCertifyApprove && !awaitingPeer && !canLock && !canRollFwd && !rollFwdDone && <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">—</span>}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {hasMore && (
          <div>
            <button type="button" className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50" onClick={() => loadPeriods({ cursor: nextCursor, append: true })} disabled={isLoadingMore}>
              {isLoadingMore ? 'Loading more…' : 'Load More'}
            </button>
          </div>
        )}
      </div>

      {/* Side panel — period detail */}
      {selectedPeriod && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setSelectedPeriod(null)} />
          <div className="fixed top-0 right-0 z-40 h-full w-80 bg-[rgb(var(--color-surface))] border-l border-[rgb(var(--color-border))] shadow-xl flex flex-col">
            <div className="flex items-start justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
              <div>
                <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Period Detail</p>
                <p className="text-tok-base font-semibold text-[rgb(var(--color-text))]">{selectedPeriod.period_code}</p>
              </div>
              <button onClick={() => setSelectedPeriod(null)} className="rounded-lg p-1 hover:bg-[rgb(var(--color-surface-muted))]"><X className="h-4 w-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {[
                ['Label', selectedPeriod.period_label],
                ['Start Date', selectedPeriod.period_start_date],
                ['End Date', selectedPeriod.period_end_date],
                ['Type', selectedPeriod.period_type],
                ['Status', selectedPeriod.period_status],
                ['Active', selectedPeriod.is_active ? 'Active' : 'Inactive'],
                ['Period ID', selectedPeriod.fiscal_period_id],
              ].map(([label, val]) => (
                <div key={label}>
                  <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">{label}</p>
                  <p className="text-tok-sm text-[rgb(var(--color-text))]">{val ?? '—'}</p>
                </div>
              ))}
              {/* S648 — side panel: Certify & Close dual-approval + Lock + Roll Forward */}
              <div className="pt-3 flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
                {(() => {
                  const spSubmittedBy    = selectedPeriod.certify_submitted_by ?? null
                  const spCanSubmit      = selectedPeriod.period_status === PERIOD_STATUS.OPEN && !spSubmittedBy
                  const spCanApprove     = selectedPeriod.period_status === PERIOD_STATUS.OPEN && !!spSubmittedBy && currentUserId !== String(spSubmittedBy)
                  const spAwaitingPeer   = selectedPeriod.period_status === PERIOD_STATUS.OPEN && !!spSubmittedBy && currentUserId === String(spSubmittedBy)
                  const spCanLock        = selectedPeriod.period_status === PERIOD_STATUS.CLOSED
                  const spRollFwdDone    = selectedPeriod.period_status === PERIOD_STATUS.LOCKED && _nextPeriod(selectedPeriod)?.is_active === true
                  const spCanRollFwd     = selectedPeriod.period_status === PERIOD_STATUS.LOCKED && !spRollFwdDone
                  return (
                    <>
                      {spCanSubmit    && <button type="button" className="rounded-lg bg-[rgb(var(--color-warning))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-warning))]/80 disabled:opacity-50" onClick={() => { openConfirmAction(CONFIRM_ACTION_TYPE.CERTIFY_SUBMIT, selectedPeriod); setSelectedPeriod(null) }} disabled={isSubmitting}>Certify &amp; Close</button>}
                      {spCanApprove   && <button type="button" className="rounded-lg bg-[rgb(var(--color-danger))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-danger))]/80 disabled:opacity-50" onClick={() => { openConfirmAction(CONFIRM_ACTION_TYPE.CERTIFY_APPROVE, selectedPeriod); setSelectedPeriod(null) }} disabled={isSubmitting}>Approve &amp; Close</button>}
                      {spAwaitingPeer && <span className="inline-flex items-center gap-1 rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-2 py-1 text-tok-xs text-[rgb(var(--color-warning))]">⏳ Awaiting peer approval</span>}
                      {spSubmittedBy  && selectedPeriod.period_status === PERIOD_STATUS.OPEN && (
                        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Submitted by: {spSubmittedBy.slice(0, 8)}…</p>
                      )}
                      {spCanLock      && <button type="button" className="rounded-lg bg-[rgb(var(--color-danger))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-danger))]/80 disabled:opacity-50" onClick={() => { openConfirmAction(CONFIRM_ACTION_TYPE.LOCK, selectedPeriod); setSelectedPeriod(null) }} disabled={isSubmitting}>Lock Period</button>}
                      {spCanRollFwd   && <button type="button" className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary))]/80 disabled:opacity-50" onClick={() => { openConfirmAction(CONFIRM_ACTION_TYPE.ROLL_FORWARD, selectedPeriod); setSelectedPeriod(null) }} disabled={isSubmitting}>Roll Forward</button>}
                      {spRollFwdDone  && <span className="text-tok-xs text-[rgb(var(--color-success))]">✓ Roll-forward completed</span>}
                    </>
                  )
                })()}
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  )
}
